import {
    VaultItemCard,
    VaultFilters,
    type VaultFiltersState,
} from '@/components/Vault';
import type { Item } from '@/types/items';

interface VaultTabProps {
    vaultItems: Item[];
    filteredVaultItems: Item[];
    vaultFilters: VaultFiltersState;
    onFiltersChange: (filters: VaultFiltersState) => void;
    onItemClick: (item: Item) => void;
}

/**
 * Vault door icon SVG component.
 */
function VaultDoorIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={1.5} />
            <circle cx="12" cy="12" r="4" strokeWidth={1.5} />
            <path d="M12 8V12L14 14" strokeWidth={1.5} strokeLinecap="round" />
            <circle cx="7" cy="7" r="1" fill="currentColor" />
            <circle cx="17" cy="7" r="1" fill="currentColor" />
            <circle cx="7" cy="17" r="1" fill="currentColor" />
            <circle cx="17" cy="17" r="1" fill="currentColor" />
        </svg>
    );
}

export function VaultTab({
    vaultItems,
    filteredVaultItems,
    vaultFilters,
    onFiltersChange,
    onItemClick,
}: VaultTabProps) {
    return (
        <div className="max-w-6xl">
            {/* Vault Header */}
            <div
                className="relative mb-6 p-4 rounded-lg overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #3D2512 0%, #2A1A0D 50%, #1F1409 100%)',
                    boxShadow: 'inset 0 2px 4px rgba(139, 105, 20, 0.2), 0 4px 12px rgba(0,0,0,0.4)',
                    border: '3px solid #4A2E15',
                }}
            >
                {/* Metal plate texture */}
                <div
                    className="absolute inset-0 opacity-10 pointer-events-none"
                    style={{
                        backgroundImage: `repeating-linear-gradient(
                            0deg,
                            transparent 0px,
                            transparent 3px,
                            rgba(255,255,255,0.05) 3px,
                            rgba(255,255,255,0.05) 4px
                        )`,
                    }}
                />

                {/* Corner rivets */}
                <div className="absolute top-2 left-2 w-2 h-2 rounded-full" style={{ background: 'radial-gradient(circle at 30% 30%, #8B7355, #4A2E15)' }} />
                <div className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ background: 'radial-gradient(circle at 30% 30%, #8B7355, #4A2E15)' }} />
                <div className="absolute bottom-2 left-2 w-2 h-2 rounded-full" style={{ background: 'radial-gradient(circle at 30% 30%, #8B7355, #4A2E15)' }} />
                <div className="absolute bottom-2 right-2 w-2 h-2 rounded-full" style={{ background: 'radial-gradient(circle at 30% 30%, #8B7355, #4A2E15)' }} />

                <div className="relative flex items-center gap-3">
                    <VaultDoorIcon className="w-8 h-8 text-amber-600" />
                    <div>
                        <h2
                            className="text-2xl font-serif font-bold"
                            style={{ color: '#D4A574' }}
                        >
                            Storage Vault
                        </h2>
                        <p
                            className="text-sm font-serif"
                            style={{ color: '#A67C52' }}
                        >
                            Items stored here await assignment to a character.
                        </p>
                    </div>
                </div>
            </div>

            {/* Vault Filters */}
            {vaultItems.length > 0 && (
                <div className="mb-6 mt-4">
                    <VaultFilters
                        filters={vaultFilters}
                        onFiltersChange={onFiltersChange}
                        totalCount={vaultItems.length}
                        filteredCount={filteredVaultItems.length}
                    />
                </div>
            )}

            {/* Vault Storage Area */}
            <div
                className="relative rounded-lg overflow-hidden"
                style={{
                    background: 'linear-gradient(180deg, #1a1408 0%, #0f0c06 100%)',
                    boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.4)',
                    border: '4px solid #3D2512',
                    minHeight: '400px',
                }}
            >
                {/* Stone wall texture */}
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{
                        backgroundImage: `
                            repeating-linear-gradient(
                                90deg,
                                transparent 0px,
                                transparent 60px,
                                rgba(139, 105, 20, 0.1) 60px,
                                rgba(139, 105, 20, 0.1) 62px
                            ),
                            repeating-linear-gradient(
                                0deg,
                                transparent 0px,
                                transparent 40px,
                                rgba(139, 105, 20, 0.08) 40px,
                                rgba(139, 105, 20, 0.08) 42px
                            )
                        `,
                    }}
                />

                {/* Wooden floor effect at bottom */}
                <div
                    className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, transparent 0%, rgba(74, 46, 21, 0.4) 100%)',
                    }}
                />

                {/* Content area */}
                <div className="relative p-6">
                    {vaultItems.length === 0 ? (
                        <div
                            className="flex flex-col items-center justify-center py-16 rounded-lg"
                            style={{
                                background: 'linear-gradient(135deg, rgba(61, 37, 18, 0.4) 0%, rgba(31, 20, 9, 0.4) 100%)',
                                border: '2px dashed rgba(139, 105, 20, 0.3)',
                            }}
                        >
                            {/* Empty vault icon - wooden crate with dust */}
                            <div className="relative mb-4">
                                <svg
                                    className="w-20 h-20"
                                    viewBox="0 0 80 80"
                                    fill="none"
                                >
                                    {/* Crate body */}
                                    <rect
                                        x="15"
                                        y="20"
                                        width="50"
                                        height="40"
                                        rx="2"
                                        fill="#4A2E15"
                                        stroke="#3D2512"
                                        strokeWidth="2"
                                    />
                                    {/* Horizontal planks */}
                                    <line x1="15" y1="33" x2="65" y2="33" stroke="#3D2512" strokeWidth="1" />
                                    <line x1="15" y1="47" x2="65" y2="47" stroke="#3D2512" strokeWidth="1" />
                                    {/* Vertical supports */}
                                    <rect x="25" y="20" width="4" height="40" fill="#5D3A1A" />
                                    <rect x="51" y="20" width="4" height="40" fill="#5D3A1A" />
                                    {/* Lid open */}
                                    <path
                                        d="M15 20 L15 12 L65 12 L65 20"
                                        fill="#5D3A1A"
                                        stroke="#3D2512"
                                        strokeWidth="2"
                                    />
                                    {/* Empty interior shadow */}
                                    <rect
                                        x="20"
                                        y="22"
                                        width="40"
                                        height="8"
                                        fill="rgba(0,0,0,0.3)"
                                    />
                                    {/* Dust particles */}
                                    <circle cx="30" cy="65" r="1" fill="rgba(139, 105, 20, 0.4)" />
                                    <circle cx="45" cy="67" r="1.5" fill="rgba(139, 105, 20, 0.3)" />
                                    <circle cx="55" cy="64" r="1" fill="rgba(139, 105, 20, 0.4)" />
                                </svg>
                            </div>
                            <p
                                className="font-serif text-lg"
                                style={{ color: '#A67C52' }}
                            >
                                Your vault is empty.
                            </p>
                            <p
                                className="font-serif text-sm mt-1"
                                style={{ color: '#6B5B47' }}
                            >
                                Items awarded to you will appear here until assigned to a character.
                            </p>
                        </div>
                    ) : filteredVaultItems.length === 0 ? (
                        <div
                            className="flex flex-col items-center justify-center py-16 rounded-lg"
                            style={{
                                background: 'linear-gradient(135deg, rgba(61, 37, 18, 0.4) 0%, rgba(31, 20, 9, 0.4) 100%)',
                                border: '2px dashed rgba(139, 105, 20, 0.3)',
                            }}
                        >
                            <svg
                                className="w-16 h-16 mb-4"
                                style={{ color: '#6B5B47' }}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                            <p
                                className="font-serif text-lg"
                                style={{ color: '#A67C52' }}
                            >
                                No items match your filters.
                            </p>
                            <p
                                className="font-serif text-sm mt-1"
                                style={{ color: '#6B5B47' }}
                            >
                                Try adjusting your filter criteria or clear the filters.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Shelving effect - decorative top beam */}
                            <div
                                className="absolute top-0 left-0 right-0 h-2 pointer-events-none"
                                style={{
                                    background: 'linear-gradient(180deg, #5D3A1A 0%, #4A2E15 100%)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                                }}
                            />

                            {/* Items grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 pt-2">
                                {filteredVaultItems.map((item) => (
                                    <VaultItemCard
                                        key={item.id}
                                        item={item}
                                        onClick={() => onItemClick(item)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Vault floor/base */}
            <div
                className="h-3 rounded-b-lg -mt-1"
                style={{
                    background: 'linear-gradient(180deg, #3D2512 0%, #2A1A0D 100%)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                }}
            />
        </div>
    );
}
